// frontend/src/components/profile/ServiceHistory.js
import React, { useState, useEffect } from 'react';
import { servicesApi } from '../../api/services';
import { serviceOpsApi } from '../../api/serviceOps';
import { apiClient } from '../../api'; 
import helpers from '../../utils/helpers';
import { 
    FaCalendarAlt, FaClock, FaMapMarkerAlt, FaFileInvoice, 
    FaTimes, FaCheckCircle, FaCreditCard, FaTruck, FaTools, FaHome, FaInfoCircle, FaExclamationTriangle, FaSpinner, FaFileContract 
} from 'react-icons/fa';
import { useNavigate } from 'react-router-dom';

function ServiceHistory() {
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const navigate = useNavigate();

  // Receipt Modal State
  const [receiptModalOpen, setReceiptModalOpen] = useState(false);
  const [selectedRecord, setSelectedRecord] = useState(null);
  const [loadingRecord, setLoadingRecord] = useState(false);

  // Quote Modal State
  const [quoteModalOpen, setQuoteModalOpen] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [loadingQuote, setLoadingQuote] = useState(false);
  const [processingQuote, setProcessingQuote] = useState(false);

  // Return Job Modal State
  const [returnModalOpen, setReturnModalOpen] = useState(false);
  const [returnBooking, setReturnBooking] = useState(null);
  const [returnReason, setReturnReason] = useState('');
  const [processingReturn, setProcessingReturn] = useState(false);

  useEffect(() => {
    fetchHistory();
  }, []);

  const fetchHistory = async () => {
    try {
      const data = await servicesApi.getBookingHistory();
      setBookings(data);
    } catch (err) {
      console.error("Error fetching service history:", err);
      setError("Failed to load service history.");
    } finally {
      setLoading(false);
    }
  };

  const handleViewReceipt = async (recordId) => {
      setReceiptModalOpen(true);
      setLoadingRecord(true);
      try {
          const record = await serviceOpsApi.getServiceRecord(recordId);
          setSelectedRecord(record);
      } catch (err) {
          alert("Failed to load receipt details.");
          setReceiptModalOpen(false);
      } finally {
          setLoadingRecord(false);
      }
  };

  const handleViewQuote = async (quoteId) => {
      setQuoteModalOpen(true);
      setLoadingQuote(true);
      try {
          const quote = await serviceOpsApi.getQuote(quoteId);
          setSelectedQuote(quote);
      } catch (err) {
          alert("Failed to load quote.");
          setQuoteModalOpen(false);
      } finally {
          setLoadingQuote(false);
      }
  };

  const handleQuoteResponse = async (action) => {
      let reason = null;
      if (action === 'reject') {
          reason = prompt("Please provide a reason for rejection:");
          if (!reason) return;
      } else {
          if (!window.confirm("Approve this quote? Work will begin immediately.")) return;
      }

      setProcessingQuote(true);
      try {
          await serviceOpsApi.respondToQuote(selectedQuote.id, action, reason);
          alert(action === 'approve' ? "Quote Approved! Work will start soon." : "Quote Rejected.");
          setQuoteModalOpen(false);
          fetchHistory();
      } catch (err) {
          alert("Failed to update quote.");
      } finally {
          setProcessingQuote(false);
      }
  };

  const handlePayNow = (invoiceId) => {
    if (invoiceId) {
      navigate(`/pay/${invoiceId}`);
    } else {
      alert("Error: Invoice ID is missing for this booking.");
    }
  };

  // --- RETURN JOB HANDLERS ---
  const handleOpenReturn = (booking) => {
      setReturnBooking(booking);
      setReturnReason('');
      setReturnModalOpen(true);
  };

  const handleSubmitReturn = async (e) => {
      e.preventDefault();
      if (!returnReason.trim()) return alert("Please describe the issue.");

      setProcessingReturn(true);
      try {
          await apiClient.post('/services/return-job', {
              original_booking_id: returnBooking.id,
              description: returnReason
          });
          alert("Return Job Created. Our team will contact you immediately.");
          setReturnModalOpen(false);
          fetchHistory(); 
      } catch (err) {
          const msg = err.data?.message || err.message || "Failed to create return job.";
          alert(`Error: ${msg}`);
      } finally {
          setProcessingReturn(false);
      }
  };

  const isEligibleForReturn = (booking) => {
      if (booking.service_stage !== 'completed') return false;
      if (!booking.updated_at) return false;
      const completionDate = new Date(booking.updated_at);
      const now = new Date();
      const diffTime = Math.abs(now - completionDate);
      const diffDays = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
      return diffDays <= 30; 
  };

  const getStatusBadge = (stage) => {
    const styles = {
      pending_review: "bg-yellow-100 text-yellow-800",
      quote_sent: "bg-blue-100 text-blue-800",
      quote_rejected: "bg-red-100 text-red-800",
      work_in_progress: "bg-purple-100 text-purple-800",
      awaiting_payment: "bg-orange-100 text-orange-800",
      payment_confirmed: "bg-blue-100 text-blue-800",
      scheduled: "bg-gray-100 text-gray-800",
      completed: "bg-green-100 text-green-800",
      cancelled: "bg-red-100 text-red-800"
    };
    const label = stage ? stage.replace(/_/g, ' ') : 'Unknown';
    return (
      <span className={`inline-block px-2 py-1 rounded-full text-xs font-bold uppercase ${styles[stage] || "bg-gray-100"}`}>
        {label}
      </span>
    );
  };

  const getServiceLabel = (type) => {
      switch(type) {
          case 'home_service': return 'Home Service';
          case 'roadside_assistance': return 'Roadside Assistance';
          case 'return_job': return 'Warranty Return';
          default: return 'Workshop Visit';
      }
  };

  const getServiceIcon = (type) => {
      switch(type) {
          case 'home_service': return <FaHome className="text-green-500" />;
          case 'roadside_assistance': return <FaTruck className="text-red-500" />;
          case 'return_job': return <FaExclamationTriangle className="text-orange-500" />;
          default: return <FaTools className="text-blue-500" />;
      }
  };

  if (loading) return <div className="text-center py-10">Loading history...</div>;

  return (
    <div className="space-y-4">
      
      {/* --- TOOLKIT --- */}
      <div className="bg-blue-50 border-l-4 border-blue-500 p-4 mb-6 rounded-r-lg shadow-sm flex items-start gap-3">
          <FaInfoCircle className="text-blue-500 text-xl mt-1 flex-shrink-0" />
          <div>
              <h4 className="font-bold text-blue-800 text-sm">Track Your Repairs</h4>
              <p className="text-sm text-blue-700">
                  View the status of your bookings. <strong>Review and Approve Quotes</strong> before work starts. Access digital receipts for completed jobs.
              </p>
          </div>
      </div>

      {error && (
        <div className="bg-red-100 text-red-700 p-3 rounded mb-4">
          {error}
        </div>
      )}

      {bookings.length === 0 && !error ? (
        <div className="text-center py-10 bg-gray-50 rounded-lg border border-dashed border-gray-300">
          <p className="text-gray-500 mb-4">No service bookings found.</p>
          <a href="/services/schedule" className="text-primary hover:underline font-medium">
            Book a Service
          </a>
        </div>
      ) : (
        bookings.map((booking) => (
          <div key={booking.id} className={`bg-white border rounded-lg p-4 shadow-sm hover:shadow-md transition-shadow ${booking.is_return_job ? 'border-orange-300 bg-orange-50' : 'border-gray-200'}`}>
            
            {/* --- HEADER SECTION --- */}
            <div className="flex flex-col md:flex-row justify-between items-start mb-3 gap-3">
              <div className="w-full md:w-auto">
                <div className="flex flex-wrap items-center gap-2">
                  {getServiceIcon(booking.service_type)}
                  <h3 className="font-bold text-gray-800 text-lg">
                    {getServiceLabel(booking.service_type)}
                  </h3>
                  {getStatusBadge(booking.service_stage || booking.status)}
                </div>
                <p className="text-xs text-gray-500 font-mono mt-1">Ref: {booking.booking_reference}</p>
              </div>
              
              <div className="w-full md:w-auto flex flex-row md:flex-col justify-between md:justify-end items-center md:items-end bg-gray-50 md:bg-transparent p-2 md:p-0 rounded border md:border-0 border-gray-100">
                <div className="flex items-center text-gray-700 font-medium gap-2 text-sm">
                  <FaCalendarAlt className="text-primary" />
                  {new Date(booking.preferred_date).toLocaleDateString()}
                </div>
                <div className="text-xs text-gray-500 md:mt-1 flex items-center gap-1">
                  <FaClock /> {booking.preferred_time_slot}
                </div>
              </div>
            </div>

            <div className="bg-gray-50 p-3 rounded-md mb-3 text-sm">
              <p className="font-semibold text-gray-700 mb-1">Vehicle:</p>
              <p className="text-gray-600">{booking.vehicle_info_snapshot}</p>
            </div>

            <div className="mb-3">
              <p className="text-sm text-gray-600 line-clamp-2">
                <span className="font-semibold">Issue:</span> {booking.description}
              </p>
            </div>

            <div className="flex flex-col sm:flex-row justify-between items-center border-t pt-3 gap-3">
               {(booking.service_type === 'home_service' || booking.service_type === 'roadside_assistance') ? (
                  <div className="flex items-center text-xs text-gray-500 w-full sm:w-auto">
                    <FaMapMarkerAlt className="mr-1 text-red-400 flex-shrink-0" />
                    <span className="truncate">{booking.address}</span>
                  </div>
               ) : <div className="hidden sm:block"></div>}

               <div className="flex gap-2 w-full sm:w-auto justify-end flex-wrap">
                  
                  {/* QUOTE BUTTON */}
                  {booking.service_stage === 'quote_sent' && booking.quote_id && (
                      <button 
                          onClick={() => handleViewQuote(booking.quote_id)}
                          className="flex-1 sm:flex-none text-sm bg-blue-600 text-white px-4 py-2 rounded border border-blue-700 hover:bg-blue-700 flex items-center justify-center gap-2 font-bold shadow-sm animate-pulse"
                      >
                          <FaFileContract /> Review Quote
                      </button>
                  )}

                  {booking.service_stage === 'awaiting_payment' && booking.invoice_id && (
                      <button 
                          onClick={() => handlePayNow(booking.invoice_id)}
                          className="flex-1 sm:flex-none text-sm bg-green-600 text-white px-4 py-2 rounded border border-green-700 hover:bg-green-700 flex items-center justify-center gap-2 font-bold shadow-sm"
                      >
                          <FaCreditCard /> Pay Now
                      </button>
                  )}

                  {booking.service_stage === 'completed' && booking.service_record_id && (
                      <button 
                          onClick={() => handleViewReceipt(booking.service_record_id)}
                          className="flex-1 sm:flex-none text-sm bg-blue-50 text-blue-700 px-3 py-2 rounded border border-blue-200 hover:bg-blue-100 flex items-center justify-center gap-2 font-bold"
                      >
                          <FaFileInvoice /> View Receipt
                      </button>
                  )}

                  {isEligibleForReturn(booking) && !booking.is_return_job && (
                      <button 
                          onClick={() => handleOpenReturn(booking)}
                          className="flex-1 sm:flex-none text-xs bg-red-50 text-red-600 px-3 py-2 rounded border border-red-200 hover:bg-red-100 flex items-center justify-center gap-1 font-bold"
                          title="Report an issue with this repair"
                      >
                          <FaExclamationTriangle /> Report Issue
                      </button>
                  )}
               </div>
            </div>
            
          </div>
        ))
      )}

      {/* --- QUOTE MODAL --- */}
      {quoteModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b bg-blue-50">
                      <h3 className="text-xl font-bold text-blue-800 flex items-center gap-2">
                          <FaFileContract /> Service Quote
                      </h3>
                      <button onClick={() => setQuoteModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto">
                      {loadingQuote ? (
                          <div className="text-center py-10"><FaSpinner className="animate-spin text-2xl text-primary" /> Loading Quote...</div>
                      ) : selectedQuote ? (
                          <div className="space-y-4 text-sm text-gray-700">
                              <div className="bg-yellow-50 p-3 rounded border border-yellow-200 text-xs text-yellow-800">
                                  <FaInfoCircle className="inline mr-1" />
                                  Please review the estimated costs below. Work will only begin after you approve this quote.
                              </div>

                              <table className="w-full text-left border-collapse">
                                  <thead>
                                      <tr className="bg-gray-100 text-xs">
                                          <th className="p-2">Item / Service</th>
                                          <th className="p-2 text-center">Qty</th>
                                          <th className="p-2 text-right">Price</th>
                                      </tr>
                                  </thead>
                                  <tbody>
                                      {selectedQuote.items.map((item, idx) => (
                                          <tr key={idx} className="border-b last:border-0">
                                              <td className="p-2">
                                                  <span className="font-bold block">{item.name}</span>
                                                  <span className="text-xs text-gray-500 capitalize">{item.type}</span>
                                              </td>
                                              <td className="p-2 text-center">{item.quantity}</td>
                                              <td className="p-2 text-right">{helpers.formatCurrency(item.unit_price * item.quantity, 'NGN')}</td>
                                          </tr>
                                      ))}
                                  </tbody>
                              </table>

                              {selectedQuote.notes && (
                                  <div className="bg-gray-50 p-3 rounded border">
                                      <p className="font-bold text-xs text-gray-500 uppercase">Notes:</p>
                                      <p>{selectedQuote.notes}</p>
                                  </div>
                              )}

                              <div className="border-t pt-4 flex justify-between items-center text-lg">
                                  <span className="font-bold">Total Estimate:</span>
                                  <span className="font-bold text-primary">{helpers.formatCurrency(selectedQuote.total_amount, 'NGN')}</span>
                              </div>
                          </div>
                      ) : (
                          <div className="text-red-500 text-center">Failed to load quote.</div>
                      )}
                  </div>

                  <div className="p-4 border-t bg-gray-50 flex gap-3 justify-end">
                      <button 
                          onClick={() => handleQuoteResponse('reject')} 
                          disabled={processingQuote}
                          className="px-4 py-2 bg-red-100 text-red-700 rounded font-bold hover:bg-red-200"
                      >
                          Reject
                      </button>
                      <button 
                          onClick={() => handleQuoteResponse('approve')} 
                          disabled={processingQuote}
                          className="px-6 py-2 bg-green-600 text-white rounded font-bold hover:bg-green-700 flex items-center gap-2"
                      >
                          {processingQuote ? <FaSpinner className="animate-spin" /> : <FaCheckCircle />} Approve & Start Work
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* --- RECEIPT MODAL (Existing) --- */}
      {receiptModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-lg max-h-[90vh] flex flex-col">
                  <div className="flex justify-between items-center p-6 border-b bg-gray-50">
                      <h3 className="text-xl font-bold text-gray-800 flex items-center gap-2">
                          <FaCheckCircle className="text-green-500" /> Service Receipt
                      </h3>
                      <button onClick={() => setReceiptModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <div className="p-6 overflow-y-auto">
                      {loadingRecord ? (
                          <div className="text-center py-10">Loading details...</div>
                      ) : selectedRecord ? (
                          <div className="space-y-4 text-sm text-gray-700">
                              <div className="grid grid-cols-2 gap-4 border-b pb-4">
                                  <div>
                                      <span className="block text-xs text-gray-500 uppercase">Service Date</span>
                                      <span className="font-bold">{selectedRecord.service_date}</span>
                                  </div>
                                  <div>
                                      <span className="block text-xs text-gray-500 uppercase">Mileage</span>
                                      <span className="font-bold">{selectedRecord.mileage_at_service.toLocaleString()} km</span>
                                  </div>
                              </div>

                              <div>
                                  <span className="block text-xs text-gray-500 uppercase mb-1">Faults Fixed</span>
                                  <p className="bg-gray-50 p-2 rounded border">{selectedRecord.faults_fixed}</p>
                              </div>

                              <div>
                                  <span className="block text-xs text-gray-500 uppercase mb-1">Work Done</span>
                                  <p className="bg-gray-50 p-2 rounded border whitespace-pre-line">{selectedRecord.work_done_description}</p>
                              </div>

                              <div>
                                  <span className="block text-xs text-gray-500 uppercase mb-2">Parts Used</span>
                                  <table className="w-full text-left border-collapse">
                                      <thead>
                                          <tr className="bg-gray-100 text-xs">
                                              <th className="p-2">Item</th>
                                              <th className="p-2 text-center">Qty</th>
                                              <th className="p-2 text-right">Price</th>
                                          </tr>
                                      </thead>
                                      <tbody>
                                          {selectedRecord.parts_used.map((part, idx) => (
                                              <tr key={idx} className="border-b last:border-0">
                                                  <td className="p-2">{part.part_name}</td>
                                                  <td className="p-2 text-center">{part.quantity}</td>
                                                  <td className="p-2 text-right">{helpers.formatCurrency(part.unit_price * part.quantity, 'NGN')}</td>
                                              </tr>
                                          ))}
                                      </tbody>
                                  </table>
                              </div>

                              <div className="border-t pt-4 flex justify-between items-center text-lg">
                                  <span className="font-bold">Total Cost:</span>
                                  <span className="font-bold text-primary">{helpers.formatCurrency(selectedRecord.total_cost, 'NGN')}</span>
                              </div>
                          </div>
                      ) : (
                          <div className="text-red-500 text-center">Failed to load record.</div>
                      )}
                  </div>

                  <div className="p-4 border-t bg-gray-50 text-center">
                      <button onClick={() => window.print()} className="text-blue-600 hover:underline text-sm">Print Receipt</button>
                  </div>
              </div>
          </div>
      )}

      {/* --- RETURN JOB MODAL (Existing) --- */}
      {returnModalOpen && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black bg-opacity-50 p-4">
              <div className="bg-white rounded-lg shadow-xl w-full max-w-md p-6 animate-fadeInUp">
                  <div className="flex justify-between items-center mb-4 border-b pb-2">
                      <h3 className="text-lg font-bold text-red-600 flex items-center gap-2">
                          <FaExclamationTriangle /> Report Issue / Return
                      </h3>
                      <button onClick={() => setReturnModalOpen(false)}><FaTimes /></button>
                  </div>
                  
                  <p className="text-sm text-gray-600 mb-4">
                      We are sorry you are experiencing issues with your recent service. Please describe the problem below, and we will schedule a priority check-up.
                  </p>

                  <form onSubmit={handleSubmitReturn}>
                      <div className="mb-4">
                          <label className="block text-xs font-bold text-gray-500 uppercase mb-1">Description of Issue</label>
                          <textarea 
                              value={returnReason}
                              onChange={e => setReturnReason(e.target.value)}
                              className="w-full p-3 border rounded focus:ring-2 focus:ring-red-500 outline-none"
                              rows="4"
                              placeholder="e.g. The noise came back after 2 days..."
                              required
                          ></textarea>
                      </div>

                      <div className="flex justify-end gap-2">
                          <button type="button" onClick={() => setReturnModalOpen(false)} className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded">Cancel</button>
                          <button 
                              type="submit" 
                              disabled={processingReturn} 
                              className="bg-red-600 text-white px-4 py-2 rounded font-bold hover:bg-red-700 flex items-center gap-2"
                          >
                              {processingReturn ? <FaSpinner className="animate-spin" /> : "Submit Report"}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

    </div>
  );
}

export default ServiceHistory;